import { InsuranceByAvailableDoctor } from 'redux/bookingFormSlice';

interface ValidatePractitionerInsuranceParams {
  doctors: InsuranceByAvailableDoctor[];
  practitionerId: string;
  currentInsuranceCarrierId: string | null;
  isPayForMyself: boolean;
  isPreferredDoctor: boolean;
  isExamCleaningService: boolean;
  dentistId?: string;
}

const getDoctorInsurancesOfDentists = ({
  isExamCleaningService,
  practitionerId: hygienistId,
  dentistId,
  insurancesByAvailableDoctors,
}: {
  isExamCleaningService: boolean;
  practitionerId: string;
  dentistId?: string;
  insurancesByAvailableDoctors: InsuranceByAvailableDoctor[];
}) => {
  if (!isExamCleaningService) {
    return null;
  }

  const availableBlocksByHyg = (insurancesByAvailableDoctors || [])
    .filter((item) => {
      return item.id === hygienistId;
    })
    .flatMap((item) => item.availableBlocks);

  if (availableBlocksByHyg.length) {
    const dentists = availableBlocksByHyg[0].dentists;

    const doctorInsuranceOfDentists = dentists
      .filter((item) => {
        if (dentistId) {
          return item.dentistId === dentistId;
        }

        return true;
      })
      .flatMap((item) => {
        const doctorInsurances = item.doctorInsurances || [];

        return {
          dentistId: item.dentistId,
          doctorInsurances,
        };
      });

    return doctorInsuranceOfDentists;
  }

  return [];
};

const checkExistingPractitionerAcceptInsuranceForNormalService = ({
  doctors,
  practitionerId,
  currentInsuranceCarrierId,
  isPreferredDoctor,
}: Pick<
  ValidatePractitionerInsuranceParams,
  | 'doctors'
  | 'practitionerId'
  | 'currentInsuranceCarrierId'
  | 'isPreferredDoctor'
>): {
  isAcceptedInsurance: boolean;
  doctorInsurance: {
    insuranceCarrierId: string;
    isInNetwork: boolean;
  } | null;
} => {
  if (isPreferredDoctor) {
    const doctor = doctors.find((d) => d.id === practitionerId);

    if (!doctor) {
      return {
        isAcceptedInsurance: false,
        doctorInsurance: null,
      };
    }

    const doctorInsurance = doctor.doctorInsurances.find(
      (item) => item.insuranceCarrierId === currentInsuranceCarrierId
    );

    if (!doctorInsurance) {
      return {
        isAcceptedInsurance: false,
        doctorInsurance: null,
      };
    }

    return {
      isAcceptedInsurance: true,
      doctorInsurance,
    };
  }

  const acceptingDoctors = doctors.filter((d) => {
    return (
      d.doctorInsurances.some(
        (i) => i.insuranceCarrierId === currentInsuranceCarrierId
      ) && d.id === practitionerId
    );
  });

  if (acceptingDoctors.length !== 0) {
    return {
      isAcceptedInsurance: true,
      doctorInsurance:
        acceptingDoctors[0].doctorInsurances.find(
          (item) => item.insuranceCarrierId === currentInsuranceCarrierId
        ) || null,
    };
  }

  return {
    isAcceptedInsurance: false,
    doctorInsurance: null,
  };
};

const checkExistingPractitionerAcceptInsuranceForExamAndCleaningService = ({
  dentistId,
  currentInsuranceCarrierId,
  isPreferredDentist, // true means doctor is selected
  doctorInsurancesOfDentists,
}: {
  currentInsuranceCarrierId: string;
  isPreferredDentist: boolean;
  dentistId: string | null;
  doctorInsurancesOfDentists: {
    dentistId: string;
    doctorInsurances: {
      insuranceCarrierId: string;
      isInNetwork: boolean;
    }[];
  }[];
}): {
  isAcceptedInsurance: boolean;
  doctorInsurance: {
    insuranceCarrierId: string;
    isInNetwork: boolean;
  } | null;
  dentistId: string | null;
} => {
  if (isPreferredDentist) {
    const doctor = doctorInsurancesOfDentists.find(
      (d) => d.dentistId === dentistId
    );

    if (!doctor) {
      return {
        isAcceptedInsurance: false,
        dentistId: null,
        doctorInsurance: null,
      };
    }

    const doctorInsurance = doctor.doctorInsurances.find(
      (item) => item.insuranceCarrierId === currentInsuranceCarrierId
    );

    if (!doctorInsurance) {
      return {
        isAcceptedInsurance: false,
        dentistId: null,
        doctorInsurance: null,
      };
    }

    return {
      isAcceptedInsurance: true,
      dentistId: dentistId,
      doctorInsurance,
    };
  }

  const acceptingDoctors = doctorInsurancesOfDentists.filter((d) => {
    return d.doctorInsurances.some(
      (i) => i.insuranceCarrierId === currentInsuranceCarrierId
    );
  });

  const firstDentist = acceptingDoctors[0];

  if (acceptingDoctors.length !== 0) {
    return {
      isAcceptedInsurance: true,
      dentistId: firstDentist.dentistId,
      doctorInsurance:
        firstDentist.doctorInsurances.find(
          (item) => item.insuranceCarrierId === currentInsuranceCarrierId
        ) || null,
    };
  }

  return {
    isAcceptedInsurance: false,
    dentistId: null,
    doctorInsurance: null,
  };
};

export const checkExistingPractitionerAcceptInsurance = ({
  doctors,
  practitionerId,
  currentInsuranceCarrierId,
  isPayForMyself,
  isPreferredDoctor,
  isExamCleaningService,
  dentistId,
}: ValidatePractitionerInsuranceParams): {
  isAcceptedInsurance: boolean;
  dentistId?: string | null;
  doctorInsurance: { insuranceCarrierId: string; isInNetwork: boolean } | null;
} => {
  if (isPayForMyself || !currentInsuranceCarrierId) {
    return {
      isAcceptedInsurance: true,
      doctorInsurance: null,
    };
  }

  const doctorInsurancesOfDentists = getDoctorInsurancesOfDentists({
    isExamCleaningService,
    practitionerId,
    dentistId,
    insurancesByAvailableDoctors: doctors,
  });

  const isSplitScheduling =
    doctors.find((item) => item.id === practitionerId)?.availableBlocks?.[0]
      ?.isSplitScheduling || false;

  if (!isExamCleaningService || (isExamCleaningService && !isSplitScheduling)) {
    return checkExistingPractitionerAcceptInsuranceForNormalService({
      doctors,
      practitionerId,
      currentInsuranceCarrierId,
      isPreferredDoctor,
    });
  }

  return checkExistingPractitionerAcceptInsuranceForExamAndCleaningService({
    dentistId: dentistId || null,
    currentInsuranceCarrierId,
    isPreferredDentist: !!dentistId,
    doctorInsurancesOfDentists: doctorInsurancesOfDentists!,
  });
};
